import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// Must match the installed pdfjs-dist version's worker build
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Same sequence/logic as the backend endpoint
const documentSequence = [
  { title: 'BILL OF EXCHANGE',           needsSignature: true },  // copyNumber={1}
  { title: 'BILL OF EXCHANGE',           needsSignature: true },  // copyNumber={2}
  { title: 'DELIVERY CHALLAN',           needsSignature: true },
  { title: 'COMMERCIAL INVOICE',         needsSignature: true },  // verify actual title text
  { title: 'DETAILS PACKING LIST',       needsSignature: true },
  { title: 'TRUCK RECEIPT',              needsSignature: true },
  { title: 'CERTIFICATE OF ORIGIN',      needsSignature: true },
  { title: 'BENEFICIARY CERTIFICATE',    needsSignature: true },
  { title: 'DELIVERY CHALLAN STATEMENT', needsSignature: false }, // verify actual title text
];

async function findDocumentBoundaries(arrayBuffer) {
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdfjsDoc = await loadingTask.promise;
  const totalPages = pdfjsDoc.numPages;

  const matchedDocs = [];
  let seqIndex = 0;

  for (let i = 1; i <= totalPages && seqIndex < documentSequence.length; i++) {
    const page = await pdfjsDoc.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items.map((item) => item.str).join(' ');

    if (pageText.includes(documentSequence[seqIndex].title)) {
      matchedDocs.push({ ...documentSequence[seqIndex], firstPage: i });
      seqIndex++;
    }
  }

  if (matchedDocs.length !== documentSequence.length) {
    console.warn(
      `Title matching found ${matchedDocs.length}/${documentSequence.length} documents. ` +
      `Check title strings against actual rendered text.`
    );
  }

  return matchedDocs.map((doc, idx) => ({
    ...doc,
    lastPage: idx < matchedDocs.length - 1 ? matchedDocs[idx + 1].firstPage - 1 : totalPages,
  }));
}

async function stampSignatures(arrayBuffer, docsWithLastPage) {
  const pdfLibDoc = await PDFDocument.load(arrayBuffer);
  const pages = pdfLibDoc.getPages();

  const helvetica = await pdfLibDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfLibDoc.embedFont(StandardFonts.HelveticaBold);

  const fontSize = 10;
  const rightMargin = 40; // pt, matches original 40pt
  const bottomMargin = 40; // pt
  const lineGap = 44; // pt, matches original margin-bottom:44pt

  for (const doc of docsWithLastPage) {
    if (!doc.needsSignature) continue; // Delivery Challan Statement — skip

    const page = pages[doc.lastPage - 1];
    const { width } = page.getSize();

    // Line 2: "Authorize Signature" (bold, right-aligned)
    const line2Text = 'Authorize Signature';
    const line2Width = helveticaBold.widthOfTextAtSize(line2Text, fontSize);
    page.drawText(line2Text, {
      x: width - rightMargin - line2Width,
      y: bottomMargin,
      size: fontSize,
      font: helveticaBold,
      color: rgb(0, 0, 0),
    });

    // Line 1: "For, Miah and Sons Industries Limited" (mixed weight, right-aligned)
    const prefixText = 'For, ';
    const boldText = 'Miah and Sons Industries Limited';
    const prefixWidth = helvetica.widthOfTextAtSize(prefixText, fontSize);
    const boldWidth = helveticaBold.widthOfTextAtSize(boldText, fontSize);
    const line1Y = bottomMargin + lineGap;
    const line1StartX = width - rightMargin - (prefixWidth + boldWidth);

    page.drawText(prefixText, {
      x: line1StartX,
      y: line1Y,
      size: fontSize,
      font: helvetica,
      color: rgb(0, 0, 0),
    });
    page.drawText(boldText, {
      x: line1StartX + prefixWidth,
      y: line1Y,
      size: fontSize,
      font: helveticaBold,
      color: rgb(0, 0, 0),
    });
  }

  return pdfLibDoc.save();
}

function CommercialPdfSignatureStamper() {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProcessing(true);
    setError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();

      // pdfjs can detach the buffer it's given, so pdf-lib needs its own copy
      const docsWithLastPage = await findDocumentBoundaries(arrayBuffer.slice(0));
      const stampedBytes = await stampSignatures(arrayBuffer.slice(0), docsWithLastPage);

      const blob = new Blob([stampedBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace(/\.pdf$/i, '') + '-stamped.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF stamping failed:', err);
      setError('Failed to process PDF. Check console for details.');
    } finally {
      setProcessing(false);
      e.target.value = '';
    }
  };

  return (
    <div style={{ display: 'inline-block', marginLeft: '8px' }}>
      <input
        id="commercial-pdf-upload"
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={processing}
      />
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={processing}
        onClick={() => document.getElementById('commercial-pdf-upload').click()}
      >
        {processing ? (
          <>
            <Spinner animation="border" size="sm" style={{ marginRight: '6px' }} />
            Stamping...
          </>
        ) : (
          'Upload & Stamp PDF'
        )}
      </Button>
      {error && <div style={{ color: 'red', fontSize: '12px' }}>{error}</div>}
    </div>
  );
}

export default CommercialPdfSignatureStamper;